// src/rms/utils/deployedVersion.js
//
// Deploy detection for long-lived RMS provider tabs.
//
// Hosting auto-deploys on every merge to main, several times a working day.
// A tab that stays open keeps running the bundle it loaded - potentially days
// old - against data and backend contracts that have moved on. These helpers
// let the provider nav compare the bundle this tab is running against the one
// the server currently serves, so the UI can prompt a refresh instead of
// degrading silently. A user-initiated reload also revalidates the HTML entry
// point with the server, which recovers a tab whose cached entry point points
// at a bundle a later deploy replaced.

/**
 * The hashed application bundle path referenced by an index.html document
 * (e.g. "/assets/index-m1z9T1n6.js"), or null when none is present.
 * Tolerant of attribute order (Vite emits type before src, but do not rely
 * on it).
 */
export function extractBundleSrc(html) {
  if (typeof html !== 'string') return null;
  const scriptTags = html.match(/<script\b[^>]*>/g) || [];
  for (const tag of scriptTags) {
    if (!/type="module"/.test(tag)) continue;
    const m = tag.match(/src="(\/assets\/[^"]+\.js)"/);
    if (m) return m[1];
  }
  return null;
}

/**
 * The hashed bundle path this tab is currently running, read from the live
 * document. Null under the dev server (which serves /src/index.js instead),
 * so callers skip polling entirely outside a built deployment.
 */
export function currentBundleSrc(doc = document) {
  const script = doc.querySelector('script[type="module"][src*="/assets/"]');
  return script ? script.getAttribute('src') : null;
}

/**
 * Fetch the currently deployed index.html (bypassing the HTTP cache) and
 * return its bundle path, or null on any failure - offline tabs and flaky
 * networks must never surface an error from a background check.
 */
export async function fetchDeployedBundleSrc() {
  try {
    const res = await fetch('/', { cache: 'no-store' });
    if (!res.ok) return null;
    return extractBundleSrc(await res.text());
  } catch (_) {
    return null;
  }
}
